<template>
  <div class="add-payment__blur-bg">
    <div class="add-payment__modal">
      <div class="add-payment__modal-glass">
        <div class="add-payment__modal-container">
          <div class="add-payment__modal-title-wrapper">
            <h4 class="add-payment__modal-title">{{ modalTitle }}</h4>
            <XIcon
              class="add-payment__modal-close-icon"
              color="#fff"
              @click="closeModal"
            />
          </div>
          <VoucherExtensionTile
            v-if="isPeriodSelection"
            :showWarning="showWarning"
            :certificateName="certificateName"
          />

          <PaymentMethodSelect
            v-if="!isPeriodSelection && !isPaymentMethodSelected"
            class="flat-scroll"
            @change="handlePaymentMethodChange"
          />

          <div class="add-payment__method-card">
            <AddCreditCardForm
              v-if="isCreditCard"
              :isLoading="isLoading"
              @change="enableSave"
              @handle-recurly-token="handleRecurlyToken"
            />
          </div>

          <PaypalSession
            v-if="isPaypal"
            @handle-recurly-token="handleRecurlyToken"
          />
        </div>
        <div
          v-if="isPeriodSelection || isPaymentMethodSelected"
          class="add-payment__total-container add-payment__modal-container"
        >
          <div class="add-payment__total-detail">
            <h5 class="add-payment__total-title">Total today: $100</h5>
            <p class="add-payment__total-description">
              Tax calculated at checkout. This amount plus tax will be charged
              to your payment method on file.
            </p>
          </div>
          <ActionLoader v-if="isLoading" name="ball-clip-rotate" />

          <button
            v-if="!isLoading && !isPaypal"
            :type="isCreditCard ? 'submit' : 'button'"
            :form="isCreditCard ? 'card-form' : null"
            class="add-payment__purchase-button btn"
            :class="{ 'btn--small': !isDesktop }"
            :disabled="!isPeriodSelection && isSaveDisabled"
            @click="handlePurchase"
          >
            {{ purchaseBtnLabel }}
          </button>
        </div>
      </div>
    </div>
    <div v-show="threeDSecure" id="recurly-3ds"></div>
  </div>
</template>
<script>
import AddCreditCardForm from '@/components/forms/AddCreditCardForm.vue'
import PaymentMethodSelect from '@/components/forms/PaymentMethodSelect.vue'
import PaypalSession from '@/components/user-dashboard/PaypalSession.vue'
import VoucherExtensionTile from '@/components/forms/VoucherExtensionTile.vue'
import { XIcon } from 'vue-feather-icons'
import { mapActions, mapGetters, mapState } from 'vuex'
import ActionLoader from '@/components/shared/ActionLoader.vue'

export default {
  name: 'ExtendVoucherModal',
  components: {
    XIcon,
    ActionLoader,
    AddCreditCardForm,
    PaypalSession,
    PaymentMethodSelect,
    VoucherExtensionTile,
  },
  inject: ['getWindowWidth'],
  props: {
    showWarning: {
      type: Boolean,
      default: false,
    },
    certificateName: {
      type: String,
      required: false,
    },
    voucherId: {
      type: String,
      required: false,
    },
    cancelCallback: {
      type: Function,
      default: () => {},
    },
  },
  data() {
    return {
      paymentMethodSelected: '',
      isSaveDisabled: true,
      isPeriodSelection: true,
      isLoading: false,
      threeDSecure: null,
    }
  },
  computed: {
    ...mapState({
      paymentInfo: state => state.subscriptions.paymentInfo,
    }),
    ...mapGetters('subscriptions', ['userHasPaymentMethod']),
    isDesktop() {
      return this.getWindowWidth() >= 1300
    },
    modalTitle() {
      if (this.isPeriodSelection) return 'Extend your voucher'

      switch (this.paymentMethodSelected) {
        case 'card':
          return 'Add Card'
        case 'paypal':
          return 'PayPal'
        case '3_d_secure':
          return '3D Secure'

        default:
          return 'Add payment method'
      }
    },
    isPaymentMethodSelected() {
      return this.paymentMethodSelected !== ''
    },
    isCreditCard() {
      return this.paymentMethodSelected === 'card'
    },
    isPaypal() {
      return this.paymentMethodSelected === 'paypal'
    },
    purchaseBtnLabel() {
      if (this.isPeriodSelection) {
        if (this.userHasPaymentMethod) return 'Buy now'
        else return 'Add payment method'
      } else {
        return 'Save payment and purchase'
      }
    },
  },
  methods: {
    ...mapActions('app', ['setError', 'setNotification']),
    ...mapActions('subscriptions', [
      'purchaseVoucherExtension',
      'getPaymentInfo',
      'updatePaymentMethod',
      'subscribePaypal',
    ]),
    handlePaymentMethodChange(method) {
      this.paymentMethodSelected = method
    },
    async handleRecurlyToken(recurlyToken) {
      const recurlyTokenId = recurlyToken.id
      try {
        this.isLoading = true
        await this.purchaseAndSave(recurlyTokenId)
        this.isLoading = false
        this.$emit('close')
      } catch (error) {
        if (
          error?.response?.data?.processor_error_code ===
          'three_d_secure_action_required'
        ) {
          this.setNotification(error.response.data.error.message)
          this.start3dsFlow(error.response.data.actionTokenId, recurlyTokenId)
        } else if (error.response.data.error.message) {
          this.setError(error.response.data.error.message)
        }
        this.cancelCallback()
        this.isLoading = false
      }
    },
    enableSave() {
      if (this.isSaveDisabled) this.isSaveDisabled = false
    },
    async purchaseAndSave(billingToken, challengeToken) {
      await this.purchaseVoucherExtension({
        voucher_id: this.voucherId || null,
        billing_token: billingToken,
        payment_challenge_token: challengeToken,
      })
      await this.updatePaymentMethod(billingToken)
      await this.getPaymentInfo()
    },
    async handlePurchase() {
      if (this.isPeriodSelection) {
        if (this.userHasPaymentMethod) {
          try {
            this.isLoading = true
            await this.purchaseVoucherExtension({
              voucher_id: this.voucherId || null,
              billing_token: null,
              payment_challenge_token: null,
            })
          } catch (error) {
            this.cancelCallback()
            this.setError(error.message)
          } finally {
            this.isLoading = false
            this.$emit('close')
          }
        } else {
          this.isPeriodSelection = false
        }
      }
    },
    closeModal() {
      this.cancelCallback()
      this.$emit('close')
    },
    async threeDSecureResolved(billingToken, challengeToken) {
      try {
        await this.purchaseAndSave(billingToken, challengeToken)
      } catch (error) {
        this.setError(error.message)
      } finally {
        this.threeDSecure.remove()
        this.$emit('close')
      }
    },
    start3dsFlow(actionTokenId, billingToken) {
      const risk = window.recurly.Risk()
      this.threeDSecure = risk.ThreeDSecure({
        actionTokenId,
      })
      this.threeDSecure.on('token', token =>
        this.threeDSecureResolved(billingToken, token.id)
      )
      this.threeDSecure.on('error', error => {
        this.$store.dispatch(
          'app/setError',
          error && error.message
            ? error.message
            : 'We were unable to verify your order through 3D-Secure. Please contact your bank.'
        )
        this.threeDSecure.remove()
      })
      this.threeDSecure.attach(document.querySelector('#recurly-3ds'))
    },
  },
}
</script>
<style lang="scss">
.add-payment {
  &__blur-bg {
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    position: fixed;
    top: 0;
    left: 0;
    z-index: 999;
  }
  &__modal {
    background-color: white;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);

    border-radius: 24px;
    background: var(--dark-brighter);
    overflow: hidden;

    width: 360px;

    @include desktop {
      width: 560px;
    }
    &-glass {
      background: var(--glass-mid-gray);
      width: 100%;
      height: 100%;
    }
    &-close-icon {
      cursor: pointer;
    }
    &-title {
      margin: 0 0 20px 0;

      font-feature-settings: 'clig' off, 'liga' off;
      text-shadow: 0px 4px 4px rgba(0, 0, 0, 0.25);

      font-family: var(--font-bold);
      font-size: 1.5rem;
      font-style: normal;
      font-weight: 700;
      line-height: 2rem;

      color: var(--white);

      @include desktop {
        margin: 0 0 30px 0;
      }

      &-wrapper {
        display: flex;
        justify-content: space-between;
        gap: 10px;
        width: 100%;
      }
    }

    &-container {
      width: 100%;
      padding: 20px;

      @include desktop {
        padding: 30px;
      }
    }
  }

  &__method {
    &-card {
      overflow: auto;
      max-height: 50vh;
    }
  }

  &__total {
    &-container {
      display: flex;
      flex-flow: column;
      align-items: center;
      gap: 15px;
      background: var(--glass-mid-gray);

      @include desktop {
        flex-flow: row;
      }
    }
    &-detail {
      flex: 1;
      margin-right: 20px;
      box-sizing: border-box;
    }
    &-title {
      margin: 0;

      color: var(--white);
      font-feature-settings: 'clig' off, 'liga' off;

      font-family: var(--font-bold);
      font-size: 1.25rem;
      font-style: normal;
      font-weight: 700;
      line-height: 1.625rem;
    }
    &-description {
      margin: 5px 0 0 0;

      color: var(--light-blue-gray);
      font-feature-settings: 'clig' off, 'liga' off;

      font-size: 0.625rem;
      font-style: normal;
      font-weight: 450;
      line-height: 0.75rem;
    }
  }
}
#recurly-3ds {
  border-radius: 24px;
  overflow: hidden;
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  height: 50%;
  width: 50%;
  margin: auto;
  z-index: 9999;
}
</style>
